import { useEffect } from 'react';
import { useStoryStore } from '@store/storyStore';
import { useAudioStore } from '@store/audioStore';
import { useSettingsStore } from '@store/settingsStore';

/**
 * AudioManager component
 * Handles all audio playback based on scene and narrative changes
 * No UI - purely functional component
 */
export function AudioManager() {
  const currentScene = useStoryStore((state) => state.currentScene);
  const currentNarrative = useStoryStore((state) => state.currentNarrative);
  const setCanAdvance = useStoryStore((state) => state.setCanAdvance);
  const { playMusic, crossfadeMusic, playVoice, stopVoice, playSFX, stopAllSFX, cleanup } = useAudioStore();
  const { masterVolume, musicVolume, sfxVolume, voiceVolume } = useSettingsStore();

  // Sync settings store volumes with audio store on mount
  useEffect(() => {
    useAudioStore.getState().setMasterVolume(masterVolume);
    useAudioStore.getState().setMusicVolume(musicVolume);
    useAudioStore.getState().setSFXVolume(sfxVolume);
    useAudioStore.getState().setVoiceVolume(voiceVolume);
  }, []);

  // Handle background music changes
  useEffect(() => {
    if (!currentScene) return;

    const musicUrl = currentScene.backgroundMusicUrl;

    if (musicUrl) {
      console.log('[AudioManager] Starting background music:', musicUrl);

      // Check if it's a different track
      const currentMusic = useAudioStore.getState().currentMusic;

      if (currentMusic?.url !== musicUrl) {
        // Crossfade if music is already playing, otherwise just play
        if (currentMusic) {
          crossfadeMusic(musicUrl, 2000); // 2 second crossfade
        } else {
          playMusic(musicUrl, { loop: true });
        }
      }
    }
  }, [currentScene?.backgroundMusicUrl, playMusic, crossfadeMusic]);

  // Handle scene sound effects
  useEffect(() => {
    console.log('[AudioManager] Scene SFX useEffect - scene:', currentScene?.id, 'soundEffects:', currentScene?.soundEffects);

    // Stop all previous SFX (both scene and narrative) when scene changes
    console.log('[AudioManager] Stopping all SFX before scene change');
    stopAllSFX();

    if (!currentScene?.soundEffects || currentScene.soundEffects.length === 0) {
      console.log('[AudioManager] No scene sound effects - returning early');
      return;
    }

    console.log('[AudioManager] Playing scene sound effects:', currentScene.soundEffects);

    // Play each scene sound effect with a specific ID prefix
    currentScene.soundEffects.forEach((sfx, index) => {
      console.log('[AudioManager] Calling playSFX for scene:', sfx.url, 'volume:', sfx.volume, 'loop:', sfx.loop);
      playSFX(sfx.url, {
        autoPlay: sfx.autoPlay ?? true,
        loop: sfx.loop ?? false,
        volume: sfx.volume ?? 1.0,
        fadeInDuration: sfx.fadeInDuration,
        fadeOutDuration: sfx.fadeOutDuration,
        startDelay: sfx.startDelay,
        playbackRate: sfx.playbackRate,
      }, `scene-sfx-${currentScene.id}-${index}`); // Use scene-specific ID
    });
  }, [currentScene?.id, playSFX, stopAllSFX]); // Only depend on scene ID, not soundEffects array

  // Handle narrative voice audio changes
  useEffect(() => {
    console.log('[AudioManager] Narrative audio useEffect - currentNarrative:', currentNarrative);

    if (!currentNarrative) {
      // Don't stop voice immediately - it might be a brief transition
      // The playVoice function will stop previous voice when new one starts
      return;
    }

    // Reset canAdvance when narrative changes
    setCanAdvance(false);

    const audioUrl = currentNarrative.audioUrl;

    if (audioUrl) {
      console.log('[AudioManager] Playing narrative audio:', audioUrl);

      // Play the narrative audio with default settings
      // The playVoice function will automatically handle ducking and stop previous voice
      playVoice(audioUrl, {
        autoPlay: true,
        volume: 1.0,
      }, () => {
        // When voice audio finishes, allow user to advance
        console.log('[AudioManager] Voice audio ended, enabling canAdvance');
        setCanAdvance(true);
      });
    } else {
      // If narrative has no audio, stop voice and allow advancing immediately
      console.log('[AudioManager] No audio for narrative, stopping voice and enabling canAdvance immediately');
      stopVoice();
      setCanAdvance(true);
    }
  }, [currentNarrative?.id, currentNarrative?.audioUrl, playVoice, stopVoice, setCanAdvance]);

  // Handle narrative sound effects and scene SFX stops
  useEffect(() => {
    console.log('[AudioManager] Narrative SFX useEffect - narrative:', currentNarrative?.id);

    // Stop specific scene SFX if requested
    if (currentNarrative?.stopSceneSoundEffects && currentNarrative.stopSceneSoundEffects.length > 0) {
      console.log('[AudioManager] Stopping scene SFX requested by narrative:', currentNarrative.stopSceneSoundEffects);
      const { activeSFX, stopSFX } = useAudioStore.getState();
      currentNarrative.stopSceneSoundEffects.forEach((urlToStop) => {
        // Find and stop all scene SFX with this URL
        activeSFX.forEach((instance, id) => {
          if (id.startsWith('scene-sfx-') && instance.url === urlToStop) {
            console.log('[AudioManager] Stopping scene SFX:', id, urlToStop);
            stopSFX(id);
          }
        });
      });
    }

    if (!currentNarrative?.soundEffects || currentNarrative.soundEffects.length === 0) {
      console.log('[AudioManager] No narrative sound effects - returning early');
      return;
    }

    console.log('[AudioManager] Playing narrative sound effects:', currentNarrative.soundEffects);

    // Play each sound effect from the narrative with a specific ID prefix
    currentNarrative.soundEffects.forEach((sfx, index) => {
      console.log('[AudioManager] Calling playSFX for narrative:', sfx.url, 'volume:', sfx.volume, 'loop:', sfx.loop, 'autoPlay:', sfx.autoPlay);
      playSFX(sfx.url, {
        autoPlay: sfx.autoPlay ?? true,
        loop: sfx.loop ?? false,
        volume: sfx.volume ?? 1.0,
        fadeInDuration: sfx.fadeInDuration,
        fadeOutDuration: sfx.fadeOutDuration,
        startDelay: sfx.startDelay,
        playbackRate: sfx.playbackRate,
      }, `narrative-sfx-${currentNarrative.id}-${index}`); // Use narrative-specific ID
    });

    // Cleanup function: stop this narrative's SFX when the narrative changes
    return () => {
      console.log('[AudioManager] Cleaning up narrative SFX for:', currentNarrative.id);
      const { activeSFX } = useAudioStore.getState();
      // Stop only the SFX that belong to this specific narrative
      activeSFX.forEach((_, id) => {
        if (id.startsWith(`narrative-sfx-${currentNarrative.id}-`)) {
          console.log('[AudioManager] Stopping narrative SFX:', id);
          useAudioStore.getState().stopSFX(id);
        }
      });
    };
  }, [currentNarrative?.id, currentNarrative?.stopSceneSoundEffects, playSFX]); // Depend on ID and stopSceneSoundEffects

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // This component renders nothing
  return null;
}
